/**
 * The approval card's words (slice 3 §8, boards AP1–AP4), kept apart from
 * the card so they're tested without a render. Pure.
 */
import { shortTime } from "../chats/format";
import { eventInstant } from "../../../worker/domain/event-time";
import type { Approval, Preview } from "./api";

type Layout<L extends Preview["layout"]> = Extract<Preview, { layout: L }>;

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** "maya@example.com, sam@example.com" → each address, trimmed. */
export const splitAddresses = (text: string) =>
  text
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

export const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/** Approve's label: what it does, in the card's words (AP1, AP4). */
export function approveLabel(a: Approval): string {
  const p = a.preview;
  switch (p.layout) {
    case "email":
      return "Approve and send";
    case "event":
      return p.emails_guests ? "Send invite" : "Add it";
    case "routine":
      return "Start it";
    case "share":
      return "Share it";
    case "delete":
      return "Delete it";
    default:
      return "Approve";
  }
}

/** The note beside the buttons, from 480 px. */
export function footNote(a: Approval): string {
  const p = a.preview;
  switch (p.layout) {
    case "email":
      return "Edit anything first. It sends what you see.";
    case "event":
      return p.emails_guests
        ? `Google emails ${p.guests.length === 1 ? p.guests[0] : "the guests"} the invite`
        : "Nobody is invited or emailed";
    case "routine":
      return "Pause or edit it any time in Routines";
    default:
      return "Nothing happens until you approve";
  }
}

/** Who it went to, for the closed line: "Daniel Okafor", "3 people". */
function whom(a: Approval): string | null {
  const p = a.preview;
  const to = p.layout === "email" ? p.to : p.layout === "share" ? p.who : [];
  if (to.length === 0) return null;
  return to.length === 1 ? to[0] : `${to.length} people`;
}

/** What a closed line is made of. */
type Said = { a: Approval; who: string; to: string | null; routine: boolean; gone: string };

/** Each state's line (AP2). */
const LINES: Partial<Record<Approval["status"], (s: Said) => string>> = {
  running: ({ who, to, routine }) =>
    `${who} approved. ${routine ? "Starting it…" : `Sending${to ? ` to ${to}` : ""}…`}`,
  done: ({ a, who, to, routine }) => {
    if (routine) return `${who} approved. Started the routine`;
    return `${who} ${a.edited ? "edited and approved" : "approved"}. ${to ? `Sent to ${to}` : "Done"}`;
  },
  failed: ({ a }) => `Not sent: ${a.error?.hint ?? "it was refused"}`,
  unknown: ({ a }) =>
    `${a.action?.app_name ?? "The app"} didn't answer, so it may have gone. The crew will check first.`,
  declined: ({ who, gone }) => `${who} asked for a change. Nothing was ${gone}.`,
  withdrawn: ({ a }) =>
    `${a.agent?.name ?? "The crew"} withdrew it${a.withdraw_reason ? `: ${a.withdraw_reason}` : "."}`,
  expired: ({ gone }) => `Nobody approved this in 7 days. Nothing was ${gone}.`,
};

/** The one line a moving or closed card shrinks to (AP2), and its time. */
export function closedLine(a: Approval, tz: string): { text: string; meta: string } {
  const routine = a.kind === "routine";
  const said: Said = {
    a,
    who: firstName(a.decided_by?.name ?? a.approver.name),
    to: whom(a),
    routine,
    gone: routine ? "started" : "sent",
  };
  const line = LINES[a.status];
  return {
    text: line ? line(said) : a.preview.title,
    meta: shortTime(a.closed_at ?? a.decided_at ?? a.created_at, tz),
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * An event's start for its date tile: the month, the day and a line
 * ("Wed 28 Oct, 12:00 pm – 5:00 pm"), with its end when it has one (a day
 * apart, its date too), in `tz`: the event's own timezone, else the
 * workspace's. A wall time with no offset is read there, as Calendar reads
 * it (Devin on #491). Null when the start isn't a date.
 */
export function dateParts(start: string | null, tz: string, end: string | null = null) {
  const at = eventInstant(start, tz);
  if (!at) return null;
  const parts = (d: Date) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(d);
  const getter = (d: Date) => {
    const ps = parts(d);
    return (t: string) => ps.find((x) => x.type === t)?.value ?? "";
  };
  const get = getter(at);
  const day = (g: typeof get) => `${g("weekday")} ${g("day")} ${g("month")}`;
  const time = (g: typeof get) => `${g("hour")}:${g("minute")} ${g("dayPeriod").toLowerCase()}`;
  const month =
    MONTHS[
      Number(new Intl.DateTimeFormat("en-GB", { timeZone: tz, month: "numeric" }).format(at)) - 1
    ];
  const until = eventInstant(end, tz);
  const g2 = until ? getter(until) : null;
  const to = g2 ? ` – ${day(g2) === day(get) ? "" : `${day(g2)}, `}${time(g2)}` : "";
  return {
    month: month ?? get("month"),
    date: get("day"),
    when: `${day(get)}, ${time(get)}${to}`,
  };
}

/**
 * The card's title once the approver changed To (Devin on #491): it names
 * who it now goes to, not who the draft named. Unchanged, the Worker's.
 */
export function titleWith(
  p: Approval["preview"],
  edited: readonly string[] | string | undefined,
): string {
  if (p.layout !== "email" || edited === undefined) return p.title;
  const to = typeof edited === "string" ? [edited] : edited;
  if (p.thread) return "Send this reply?";
  const more = to.length > 1 ? ` and ${to.length - 1} more` : "";
  return to.length ? `Send this email to ${to[0]}${more}?` : "Send this email?";
}

/** An event's time line: its day and times, and its zone when it isn't the workspace's. */
export function eventWhen(when: string, zone: string, workspaceZone: string): string {
  return zone === workspaceZone ? when : `${when} (${zone.replace(/_/g, " ")})`;
}

/** Each layout's first lines, for a row that shows only its start (Today, slice 4b §5). */
const SNIPPETS: {
  [L in Preview["layout"]]: (p: Layout<L>) => { head: string | null; body: string };
} = {
  email: (p) => ({
    head:
      [p.to.length ? `To ${p.to.join(", ")}` : null, p.subject].filter(Boolean).join(" · ") || null,
    body: p.body ?? "",
  }),
  event: (p) => ({
    head: p.summary,
    body: p.guests.length ? `Guests: ${p.guests.join(", ")}` : "No guests",
  }),
  routine: (p) => ({ head: p.when || null, body: p.does }),
  share: (p) => ({ head: p.what, body: p.who.join(", ") }),
  delete: (p) => ({ head: p.what, body: "" }),
  fields: (p) => ({ head: null, body: p.fields.map((f) => `${f.label}: ${f.value}`).join("\n") }),
};

export const approvalSnippet = (p: Preview) =>
  (SNIPPETS[p.layout] as (x: Preview) => { head: string | null; body: string })(p);
